import { useEffect, useState } from 'react';
import { GeoJSON } from 'react-leaflet';
import type { Layer, Path, PathOptions } from 'leaflet';
import type { Feature, FeatureCollection } from 'geojson';
import { loadCountyGeoJson, type CountyGeoJson } from '@/geo/geoJsonLoader';
import type { ScoredGeography } from '@/types/insights';
import { colorForBand, fillOpacityForBand } from './scoreColor';
import { useUiStore } from '@/store/uiStore';

interface CountyLayerProps {
  geographies: ScoredGeography[];
}

/** Hover outline -- thicker, darker border traced along the county's own
 * polygon, not a floating box, so what highlights is exactly that county's
 * shape. */
const HOVER_STYLE: PathOptions = { color: '#0b2545', weight: 3 };
/** Persistent outline for whichever county is currently selected (clicked) --
 * same idea as HOVER_STYLE but stays after the mouse moves away, and reads
 * a little lighter so it doesn't compete with hover feedback on a *different*
 * county. */
const SELECTED_STYLE: PathOptions = { color: '#01696f', weight: 3 };

export function CountyLayer({ geographies }: CountyLayerProps) {
  const [geoJson, setGeoJson] = useState<CountyGeoJson | null>(null);
  const selectGeo = useUiStore((s) => s.selectGeo);
  const selectedGeoId = useUiStore((s) => s.selectedGeoId);

  useEffect(() => {
    let cancelled = false;
    loadCountyGeoJson()
      .then((data) => {
        if (!cancelled) setGeoJson(data);
      })
      .catch((err) => console.error('[CountyLayer] failed to load boundaries:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  if (!geoJson) return null;

  const byGeoId = new Map(geographies.map((g) => [g.record.geoId, g]));

  // Base (non-hover) style for a feature -- selected county gets its own
  // persistent border-highlight instead of the browser's default focus
  // outline (a bounding-box square, suppressed globally in index.css)
  // once you click it.
  const baseStyle = (feature: Feature | undefined, selected: string | null): PathOptions => {
    const geoId = (feature?.properties as { geoId?: string } | undefined)?.geoId;
    const scored = geoId ? byGeoId.get(geoId) : undefined;
    if (!scored) {
      const style: PathOptions = { color: '#94a3b8', weight: 1, fillColor: '#e2e8f0', fillOpacity: 0.2 };
      return geoId && geoId === selected ? { ...style, ...SELECTED_STYLE } : style;
    }
    const band = scored.insights.hotColdScore;
    const style: PathOptions = {
      color: '#1e293b',
      weight: 1,
      fillColor: colorForBand(band),
      fillOpacity: fillOpacityForBand(band),
    };
    return geoId === selected ? { ...style, ...SELECTED_STYLE } : style;
  };

  const styleFeature = (feature?: Feature): PathOptions => baseStyle(feature, selectedGeoId);

  const onEachFeature = (feature: Feature, layer: Layer) => {
    const geoId = (feature.properties as { geoId?: string; name?: string })?.geoId;
    const name = (feature.properties as { name?: string })?.name ?? geoId;
    const scored = geoId ? byGeoId.get(geoId) : undefined;

    // Boxless label -- plain text near the cursor, no background/border, so
    // it doesn't read as a floating rectangle over the map. Full stats still
    // open in the side panel on click.
    const labelText = scored
      ? `${name} County — ${scored.insights.hotColdScore.label} (${Math.round(scored.insights.hotColdScore.value)})`
      : `${name} County — no data for this period`;
    layer.bindTooltip(labelText, { className: 'map-hover-label', sticky: true, opacity: 1 });

    // Hover feedback traces the county's own polygon border (thicker, darker
    // outline, brought to front) instead of a detached tooltip box -- the
    // highlight conforms exactly to that county's real shape. On mouseout,
    // read selection state fresh from the store (not a closure variable --
    // this handler is bound once, but the correct "resting" style can
    // change any time a *different* county gets selected while this one
    // isn't being hovered) so the county correctly falls back to its
    // selected-highlight, not a stale unselected style.
    layer.on('mouseover', () => {
      const path = layer as Path;
      path.setStyle(HOVER_STYLE);
      (path as unknown as { bringToFront: () => void }).bringToFront();
    });
    layer.on('mouseout', () => {
      (layer as Path).setStyle(baseStyle(feature, useUiStore.getState().selectedGeoId));
    });

    layer.on('click', () => {
      if (geoId) selectGeo(geoId);
    });
  };

  return (
    <GeoJSON
      // Re-mounting on selection change is unnecessary -- react-leaflet
      // re-applies the `style` prop to every existing layer when it sees a
      // new function reference here, which is exactly what we want when
      // `selectedGeoId` changes (previously-selected county's border reverts,
      // newly-selected one gets highlighted).
      data={geoJson as unknown as FeatureCollection}
      style={styleFeature}
      onEachFeature={onEachFeature}
    />
  );
}
